import { trim } from 'lodash';
import httpClient from '@common/api/httpClient';
import paramsSerializer from '@common/api/paramsSerializer';
import {
  GraphProfileContextType,
  type GraphProfilesResponse,
  type UserLevel,
} from '@common/types/api';
import {
  createGraphProfileFromResponse,
  createRequestFromGraphProfile,
  type GraphProfile,
  type PartialGraphProfile,
} from '@common/types/models/GraphProfile';

/* Currently all Diagram profiles have graphProfileContextType = null, so we cannot use
 * graphProfileContextType = GraphProfileContextType.Diagram and expect to get all diagram
 * profiles. We must do unfiltered fetching instead and filter the results manually.
 *
 * TODO: Change the above described behaviour if and when the stored profiles have been NOSONAR:S1135
 *  updated so that no profile has null value for graphProfileContextType.
 */
export async function fetchGraphProfiles(
  deviceTypeId: string,
  graphProfileContextType: GraphProfileContextType,
  userLevel: UserLevel | undefined,
  signal: AbortSignal,
  isGlobal?: boolean,
) {
  try {
    const { data = [] } = await httpClient.get<Array<GraphProfilesResponse>>(
      '/rest/v1/GraphProfiles',
      {
        params: {
          installationProfileId: deviceTypeId,
          isGlobal,
          graphProfileContextType: graphProfileContextType === GraphProfileContextType.Diagram
            ? undefined
            : graphProfileContextType,
        },
        paramsSerializer,
        signal,
      },
    );
    return data
      .filter((profile) =>
        profile.graphProfileContextType === graphProfileContextType
        || (profile.graphProfileContextType == null // Missing context type counts as Diagram
          && graphProfileContextType === GraphProfileContextType.Diagram)
      )
      .filter((profile) =>
        profile.accessLevelId == null || (userLevel && userLevel >= profile.accessLevelId)
      )
      .map(createGraphProfileFromResponse);
  } catch {
    return [];
  }
}

export async function createGraphProfile(
  partialGraphProfile: PartialGraphProfile,
): Promise<GraphProfile> {
  const response = await httpClient.post<string>(
    '/rest/v1/GraphProfiles',
    createRequestFromGraphProfile(partialGraphProfile),
  );
  return {
    ...partialGraphProfile,
    name: trim(partialGraphProfile.name),
    id: response.data,
    isGlobal: false,
  };
}

export async function updateGraphProfile(profile: GraphProfile): Promise<void> {
  if (profile.isGlobal) {
    throw new Error('Cannot update global graph profile');
  }
  const { id } = profile;
  await httpClient.put(
    `/rest/v1/GraphProfiles/${id}`,
    createRequestFromGraphProfile({
      ...profile,
      name: trim(profile.name),
    }),
  );
}

export async function deleteGraphProfile(profile: GraphProfile): Promise<void> {
  if (profile.isGlobal) {
    throw new Error('Cannot delete global graph profile');
  }
  await httpClient.delete(`/rest/v1/GraphProfiles/${profile.id}`);
}
